import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { cancelAutopay } from "../apis/subscription";
import { startPlatformCheckout, isAutopayOn, fmtChargeDate } from "../utils/platformCheckout";
import { invalidateSubscriptionAccess } from "../hooks/useSubscriptionAccess";
import { isTrialPlan, periodLabel, cyclePhrase } from "../utils/planPricing";

const AutopayBar = ({ subscription, onChanged }) => {
  const [busy, setBusy] = useState(false);
  if (!subscription || isTrialPlan(subscription) || subscription.billing_period === "1_month") return null;

  const on = isAutopayOn(subscription);
  const next = fmtChargeDate(subscription.next_charge_at) || fmtChargeDate(subscription.expires_at);

  const enable = async () => {
    setBusy(true);
    await startPlatformCheckout({
      plan: {
        plan_name: subscription.plan_name,
        plan_display_name: subscription.plan_display_name,
        catalog_version: subscription.catalog_version,
        plan_family: subscription.plan_family,
      },
      billingPeriod: subscription.billing_period,
      enableExisting: true,
      onSuccess: (sub) => {
        invalidateSubscriptionAccess();
        if (onChanged) onChanged(sub);
      },
      onSettled: () => setBusy(false),
    });
  };

  const disable = async () => {
    if (!window.confirm("Cancel auto-pay? Your plan stays active until it expires. You will not be charged again.")) {
      return;
    }
    setBusy(true);
    try {
      const res = await cancelAutopay();
      if (!res.data.success) throw new Error(res.data.error || "Failed to cancel");
      toast.success(res.data.data?.message || "Auto-pay cancelled");
      invalidateSubscriptionAccess();
      if (onChanged) onChanged(res.data.data?.subscription || null);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || "Failed to cancel auto-pay");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ping-card" style={{ marginBottom: 16, padding: "14px 16px" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div className="grow">
          <div style={{ fontWeight: 650 }}>
            Auto-pay {on ? "on" : "off"}
            {on && subscription.autopay_status === "pending" ? " · retrying" : ""}
            {on && subscription.autopay_status === "halted" ? " · halted" : ""}
          </div>
          <div style={{ fontSize: 13, color: "var(--text-3)" }}>
            {on
              ? `Next platform debit around ${next} (${periodLabel(subscription.billing_period)} cycle). Wallet and add-ons stay one-time.`
              : `Turn on auto-pay so the platform fee is collected ${cyclePhrase(subscription.billing_period)}. Wallet and add-ons stay one-time.`}
          </div>
        </div>
        {on ? (
          <button type="button" className="ping-btn ping-btn-ghost ping-btn-sm" disabled={busy} onClick={disable}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : "Cancel auto-pay"}
          </button>
        ) : (
          <button type="button" className="ping-btn ping-btn-green ping-btn-sm" disabled={busy} onClick={enable}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : "Enable auto-pay"}
          </button>
        )}
      </div>
    </div>
  );
};

export default AutopayBar;
